import React from 'react';
import ReactDOM from 'react-dom';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { getAnalyticsInstance, safeLogEvent } from 'firebase.js';
import { logDebug } from 'utils/logger';

import {
  AiSearchIcon,
  InfoIcon,
  MapIcon,
  StopwatchIcon,
  TicketIcon,
  TransportIcon,
  WeatherIcon,
} from 'assets/icons';
import { useTeamColors } from 'hooks';
import { Tooltip } from 'components';
import { trackGameInfoOpen, trackMapView } from 'utils/analytics';
// getActiveTicketProvider/buildTicketUrl/trackTicketClick (Tickets is
// unconditionally disabled below, no provider/redirect logic needed
// until a new affiliate is in place) - re-import alongside re-enabling
// isTicketsDisabled once that happens.
import { GameInfoModal } from './components/GameInfoModal';
import GameWebSearch from './components/GameWebSearch';
import WeatherInfoModal from './components/WeatherInfoModal';
import AdditionalInfoModal from './components/AdditionalInfoModal';
import {
  CenterSpacer,
  ExpandedMenuContainer,
  GameInfoContainer,
  IconContainer,
  InfoIconContainer,
} from './style';

const SEARCH_FLIGHT_URL = process.env.REACT_APP_EXPEDIA_SEARCH_FLIGHT_URL;

export const GameInfoButton = ({
  channel,
  city,
  date,
  eventLeagueName,
  eventState,
  eventType,
  headline,
  locationId,
  opponent,
  region,
  stadium,
  stadiumLocation,
  team,
  timeZone,
  zipCode,
}) => {
  // Log google analytics events
  const analytics = getAnalyticsInstance();
  const ref = React.useRef(null);
  const { mainColor, mainColorRgba, altColor } = useTeamColors();

  // Tickets are always shown but always disabled - StubHub and Viagogo
  // have both dropped their affiliate partnership with this business, and
  // there's no replacement provider yet. Hardcoded true, independent of
  // the tickets_enabled site config flag - the icon should stay visible
  // rather than disappear (matching the pattern used for other per-event
  // disabled states like isMapDisabled/isWeatherDisabled below), so a
  // stray tickets_enabled=1 doesn't silently reactivate ticket purchasing.
  // Flip this back to reading configData?.tickets_enabled once a new
  // ticket affiliate is in place.
  const isTicketsDisabled = true;

  // Get feature flags from config - handle both string '1' and integer 1
  const configData = useSelector((state) => state.configuration?.data);
  const isWeatherEnabled =
    configData?.weather_enabled === 1 || configData?.weather_enabled === '1';
  const isTravelEnabled =
    configData?.travel_enabled === 1 || configData?.travel_enabled === '1';
  const isAdditionalInfoEnabled =
    configData?.additional_info_enabled === 1 ||
    configData?.additional_info_enabled === '1';
  const isAiSearchEnabled =
    configData?.ai_search_enabled === 1 ||
    configData?.ai_search_enabled === '1';
  const isMapDirectionsEnabled =
    configData?.map_directions_enabled === 1 ||
    configData?.map_directions_enabled === '1';

  // Load initial expanded state from localStorage - GLOBAL for all events
  const [expandedMenu, setExpandedMenu] = React.useState(() => {
    try {
      const saved = localStorage.getItem('gameInfoExpandedState');
      // console.log(
      //   `[GameInfoButton] Loading GLOBAL saved state for ${team} vs ${opponent}:`,
      //   saved
      // );
      return saved === 'true';
    } catch (e) {
      console.log('[GameInfoButton] Error loading state:', e);
      return false;
    }
  });
  const [showModal, setShowModal] = React.useState(false);
  const [showWeatherModal, setShowWeatherModal] = React.useState(false);
  const [showWebSearchModal, setShowWebSearchModal] = React.useState(false);
  const [showAdditionalInfoModal, setShowAdditionalInfoModal] =
    React.useState(false);
  const autoExpandTimerRef = React.useRef(null);
  const hasTriggeredAutoExpand = React.useRef(false);

  const modalHandler = React.useCallback(() => {
    safeLogEvent('select_content', {
      content_type: 'Show game info',
      content_id: `${team} - ${opponent}`,
    });

    // Track game info open
    trackGameInfoOpen({
      team,
      opponent,
      league: eventLeagueName,
    });

    setExpandedMenu((prevState) => {
      const newState = !prevState;
      // Save to localStorage IMMEDIATELY (synchronously) before state update
      try {
        localStorage.setItem('gameInfoExpandedState', newState.toString());
        // console.log(
        //   `[GameInfoButton] IMMEDIATE save (triggered by ${team} vs ${opponent}):`,
        //   newState
        // );
        // Dispatch event to notify all other GameInfoButton instances
        window.dispatchEvent(
          new CustomEvent('gameInfoStateChanged', {
            detail: { expanded: newState },
          })
        );
      } catch (e) {
        console.log('[GameInfoButton] Error saving state:', e);
      }
      return newState;
    });
  }, [opponent, team, eventLeagueName]);

  // Listen for state changes from other GameInfoButton instances
  React.useEffect(() => {
    const handleStateChange = (e) => {
      // console.log(
      //   `[GameInfoButton] Received state change event for ${team} vs ${opponent}:`,
      //   e.detail.expanded
      // );
      setExpandedMenu(e.detail.expanded);
    };

    window.addEventListener('gameInfoStateChanged', handleStateChange);
    return () =>
      window.removeEventListener('gameInfoStateChanged', handleStateChange);
  }, [team, opponent]);

  const clickMapHandler = React.useCallback(() => {
    setShowModal((prevState) => !prevState);

    // Track map view
    if (!showModal) {
      trackMapView({
        stadium,
        city,
        region,
      });
    }
  }, [showModal, stadium, city, region]);

  const clickWeatherHandler = React.useCallback(() => {
    if (!showWeatherModal) {
      safeLogEvent('weather_button_click', {
        team,
        opponent,
        league: eventLeagueName,
        has_zip_code: !!zipCode,
      });
    }
    setShowWeatherModal((prevState) => !prevState);
  }, [showWeatherModal, team, opponent, eventLeagueName, zipCode]);

  const clickWebSearchHandler = React.useCallback(() => {
    if (!showWebSearchModal) {
      safeLogEvent('ai_search_button_click', {
        team,
        opponent,
        league: eventLeagueName,
      });
    }
    setShowWebSearchModal((prevState) => !prevState);
  }, [showWebSearchModal, team, opponent, eventLeagueName]);

  const clickAdditionalInfoHandler = React.useCallback(() => {
    if (!showAdditionalInfoModal) {
      safeLogEvent('additional_info_button_click', {
        team,
        opponent,
        league: eventLeagueName,
        has_stadium: !!stadium,
        has_city: !!city,
      });
    }
    setShowAdditionalInfoModal((prevState) => !prevState);
  }, [showAdditionalInfoModal, team, opponent, eventLeagueName, stadium, city]);

  // Removed handleClickOutside - info button should only collapse when clicked, not when clicking outside
  // This allows the expanded state to persist when navigating between events

  // Determine which features should be disabled based on available data
  const isWeatherDisabled = moment(date).isBefore(moment()) || !zipCode;
  const isAdditionalInfoDisabled = !stadium && !city;
  const isMapDisabled = !stadiumLocation?.lat || !stadiumLocation?.lng;

  // Auto-expand on first visit after location modal is dismissed
  React.useEffect(() => {
    const hasAutoExpandedBefore = localStorage.getItem(
      'gameInfoAutoExpandedOnce'
    );

    const handleLocationModalDismissed = () => {
      // Only auto-expand once on first visit
      if (!hasAutoExpandedBefore && !hasTriggeredAutoExpand.current) {
        hasTriggeredAutoExpand.current = true;
        // Wait 2 seconds after location modal dismissal
        autoExpandTimerRef.current = setTimeout(() => {
          setExpandedMenu(true);
          localStorage.setItem('gameInfoAutoExpandedOnce', 'true');
          safeLogEvent('game_info_auto_expanded', {
            context: 'first_visit_after_location_modal',
          });
        }, 2000);
      }
    };

    // Listen for location modal dismissal
    window.addEventListener(
      'locationModalDismissed',
      handleLocationModalDismissed
    );

    return () => {
      window.removeEventListener(
        'locationModalDismissed',
        handleLocationModalDismissed
      );
      if (autoExpandTimerRef.current) {
        clearTimeout(autoExpandTimerRef.current);
      }
    };
  }, [analytics]);

  return (
    <GameInfoContainer>
      <InfoIconContainer
        onClick={() => {
          // Only fire if not a touch device (prevents double-firing on mobile)
          if (!('ontouchstart' in window)) {
            modalHandler();
          }
        }}
        onTouchStart={(e) => {
          e.preventDefault();
          e.stopPropagation();
          modalHandler();
        }}
      >
        <InfoIcon fill={mainColor} stroke={altColor} />
      </InfoIconContainer>
      <ExpandedMenuContainer
        $glowColor={mainColorRgba(0.64)}
        $isOpen={expandedMenu}
        ref={ref}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Tooltip
            isActive={true}
            text="Tickets are currently unavailable for this event"
          >
            <IconContainer $isDisabled={isTicketsDisabled}>
              <TicketIcon />
            </IconContainer>
          </Tooltip>

          {isAdditionalInfoEnabled ? (
            <Tooltip
              isActive={true}
              text={
                isAdditionalInfoDisabled
                  ? 'Additional info not available for this event'
                  : 'Event Time & More Info'
              }
            >
              <IconContainer
                $isDisabled={isAdditionalInfoDisabled}
                onClick={clickAdditionalInfoHandler}
              >
                <StopwatchIcon />
              </IconContainer>
            </Tooltip>
          ) : null}

          {isAiSearchEnabled ? (
            <Tooltip isActive={true} text="Event Search (AI)">
              <IconContainer onClick={clickWebSearchHandler}>
                <AiSearchIcon />
              </IconContainer>
            </Tooltip>
          ) : null}
        </div>

        <CenterSpacer />

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {isMapDirectionsEnabled ? (
            <Tooltip
              isActive={true}
              text={
                isMapDisabled
                  ? 'Location data not available'
                  : 'Event Location & Directions'
              }
            >
              <IconContainer
                $isDisabled={isMapDisabled}
                onClick={clickMapHandler}
              >
                <MapIcon />
              </IconContainer>
            </Tooltip>
          ) : null}

          {isWeatherEnabled ? (
            <Tooltip
              isActive={true}
              text={
                isWeatherDisabled
                  ? !zipCode
                    ? 'Weather data not available'
                    : 'Weather data not available'
                  : 'Event Weather'
              }
            >
              <IconContainer
                $isDisabled={isWeatherDisabled}
                onClick={clickWeatherHandler}
              >
                <WeatherIcon />
              </IconContainer>
            </Tooltip>
          ) : null}

          {isTravelEnabled ? (
            <Tooltip isActive={true} text="Event Travel">
              <IconContainer
                onClick={() => {
                  // Track travel button click
                  safeLogEvent('travel_button_click', {
                    team,
                    opponent,
                    league: eventLeagueName,
                    has_city: !!city,
                    has_region: !!region,
                    has_stadium: !!stadium,
                  });

                  // Build Expedia redirect URL using the Creator Hub
                  // affiliate link. Old Partnerize camref link kept for
                  // reference/rollback as REACT_APP_EXPEDIA_SEARCH_FLIGHT_URL_PARTNERIZE_OLD
                  // in .env.deploy.example / .env.local.example (not read here).
                  const expediaAffiliateUrl =
                    SEARCH_FLIGHT_URL ||
                    'https://expedia.com/affiliate/AmQsnxT';

                  // Build redirect URL for Expedia
                  const redirectUrl = new URL(
                    '/redirect-handler.html',
                    window.location.origin
                  );
                  redirectUrl.searchParams.append('provider', 'expedia');
                  redirectUrl.searchParams.append(
                    'affiliate_url',
                    expediaAffiliateUrl
                  );

                  // Add location info if available
                  if (city) redirectUrl.searchParams.append('city', city);
                  if (region) redirectUrl.searchParams.append('region', region);
                  if (stadium)
                    redirectUrl.searchParams.append('venue', stadium);
                  if (date) redirectUrl.searchParams.append('date', date);

                  // Add team color for spinner styling
                  redirectUrl.searchParams.append('color', altColor);

                  logDebug('[Travel Button] Opening Expedia redirect', {
                    city,
                    region,
                    url: redirectUrl.toString(),
                  });

                  window.open(redirectUrl.toString(), '_blank');
                }}
                style={{ cursor: 'pointer' }}
              >
                <TransportIcon />
              </IconContainer>
            </Tooltip>
          ) : null}
        </div>
      </ExpandedMenuContainer>

      {showModal
        ? ReactDOM.createPortal(
            <GameInfoModal
              city={city}
              locationId={locationId}
              onClose={clickMapHandler}
              opponent={opponent}
              region={region ? region.toLowerCase() : ''}
              show={showModal}
              stadium={stadium}
              stadiumLocation={stadiumLocation}
              team={team}
              timeZone={timeZone}
              zipCode={zipCode}
            />,
            document.body
          )
        : null}

      {showWeatherModal
        ? ReactDOM.createPortal(
            <WeatherInfoModal
              city={city}
              date={date}
              onClose={clickWeatherHandler}
              show={showWeatherModal}
              stadium={stadium}
              zipCode={zipCode}
            />,
            document.body
          )
        : null}

      {showWebSearchModal
        ? ReactDOM.createPortal(
            <GameWebSearch
              eventLeagueName={eventLeagueName}
              onClose={clickWebSearchHandler}
              opponent={opponent}
              show={showWebSearchModal}
              team={team}
            />,
            document.body
          )
        : null}

      {showAdditionalInfoModal
        ? ReactDOM.createPortal(
            <AdditionalInfoModal
              channel={channel}
              city={city}
              date={date}
              eventState={eventState}
              eventType={eventType}
              headline={headline}
              onClose={clickAdditionalInfoHandler}
              opponent={opponent}
              region={region}
              show={showAdditionalInfoModal}
              stadium={stadium}
              team={team}
              timeZone={timeZone}
            />,
            document.body
          )
        : null}
    </GameInfoContainer>
  );
};
